import React, { ReactNode } from "react";
import {
  Box,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CaretRight, CaretLeft, CaretDoubleRight, CaretDoubleLeft } from "@phosphor-icons/react";

interface CallsTableWrapperProps {
  children: ReactNode;
  headerCells: ReactNode[];
  count: number;
  rowsPerPage: number;
  page: number;
  handleChangePage?: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface PageActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

const PageActions: React.FC<PageActionsProps> = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ display: "flex", gap: "4px" }}>
      <Tooltip title="First page">
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={page === 0}
          aria-label="First Page"
          size="small"
        >
          {theme.direction === "rtl" ? <CaretDoubleRight /> : <CaretDoubleLeft />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Previous Page">
        <IconButton
          onClick={handleBackButtonClick}
          disabled={page === 0}
          aria-label="Previous Page"
          size="small"
        >
          {theme.direction === "rtl" ? <CaretRight /> : <CaretLeft />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Next Page">
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="Next Page"
          size="small"
        >
          {theme.direction === "rtl" ? <CaretLeft /> : <CaretRight />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Last Page">
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="Last Page"
          size="small"
        >
          {theme.direction === "rtl" ? <CaretDoubleLeft /> : <CaretDoubleRight />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export const CallsTableWrapper: React.FC<CallsTableWrapperProps> = ({
  children,
  headerCells,
  count,
  rowsPerPage,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
}) => (
  <>
    <Box
      sx={{
        borderRadius: "16px",
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        "& .MuiPaper-root": {
          borderRadius: "16px !important",
          border: "none !important",
          boxShadow: "none !important",
        },
        "& .MuiTableContainer-root": {
          borderRadius: "16px !important",
        },
      }}
    >
      <TableContainer>
        <Table
          sx={{
            minWidth: 700,
            borderCollapse: "collapse",
            "& tr:last-child td, & tr:last-child th": { borderBottom: 0 },
            "& .MuiTableCell-head": { bgcolor: "background.paper" },
            "& .MuiTableBody-root .MuiTableRow-root": { backgroundColor: "background.paper" },
          }}
        >
          <TableHead>
            <TableRow>
              {headerCells.map((cell, index) => (
                <React.Fragment key={index}>{cell}</React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
        {count === 0 && (
          <Box
            sx={{
              width: "100%",
              padding: 10,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                height: "236px",
                width: "562px",
                padding: "64px 0px",
                textAlign: "left",
              }}
            >
              <Typography variant="h3">No Results Found</Typography>
              <Typography variant="body1">
                Try adjusting your search or filter options to find what you are looking for.
              </Typography>
            </Box>
          </Box>
        )}
      </TableContainer>
    </Box>
    {rowsPerPage && children && (
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25]}
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={PageActions}
          slotProps={{
            select: {
              variant: "outlined",
              size: "small",
            },
          }}
        />
      </Box>
    )}
  </>
);
